import { isString } from "lodash-es";

import { generateInternalId } from "src/cases/templates/internalIdUtils";
import {
  CaseBlockConfigOutput,
  CasePropertiesValue,
  CaseTemplateFormFieldsInner,
  CaseTemplateActionOutput,
  CaseTemplateAdditionalInput,
  CaseTemplateFileCategoryDefinition,
  CaseTemplateReviewRole,
  CaseTemplateTaskOutput,
  CaseTemplateVersionAttributes,
  CaseTemplateWorkflowRuleOutput,
  CaseTemplateWorkflowRuleTask,
  CaseTemplateActionTask,
  CaseTemplateWorkflowRuleAction,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import { deepOmit } from "src/utils/objectUtils";

type TemporaryFieldMarker = {
  _isNewlyAppended?: boolean;
};

type InternalIdMarker = {
  _internalId: string;
};

export type CasePropertiesValueForm = CasePropertiesValue & {
  key: string;
} & TemporaryFieldMarker;

export type FileCategoryFormValue = CaseTemplateFileCategoryDefinition & {
  key: string;
  _isDefault?: boolean;
} & TemporaryFieldMarker;

export type CaseTemplateActionFormValue = CaseTemplateActionOutput &
  TemporaryFieldMarker &
  InternalIdMarker;

export type CaseTemplateTaskFormValue = CaseTemplateTaskOutput &
  TemporaryFieldMarker &
  InternalIdMarker;

// Extended workflow rule types that include internal IDs for tracking
export type WorkflowRuleTaskWithId = CaseTemplateWorkflowRuleTask & {
  _internalId: string;
};

export type WorkflowRuleActionTaskWithId = CaseTemplateActionTask & {
  _internalId: string;
};

export type WorkflowRuleActionWithId = CaseTemplateWorkflowRuleAction & {
  _internalId: string;
  required_tasks_keys?: WorkflowRuleActionTaskWithId[] | null;
};

export type CaseTemplateWorkflowRuleFormValue = {
  [key: string]: Omit<
    CaseTemplateWorkflowRuleOutput,
    "workflow_tasks_keys" | "actions"
  > & {
    workflow_tasks_keys?: WorkflowRuleTaskWithId[] | null;
    actions: WorkflowRuleActionWithId[];
  };
};

type CaseTemplateVersionAttributesForm = Omit<
  CaseTemplateVersionAttributes,
  | "additional_inputs"
  | "case_properties"
  | "review_roles"
  | "file_categories"
  | "case_actions"
  | "workflow_rules"
  | "case_tasks"
> & {
  additional_inputs: (CaseTemplateAdditionalInput & { key: string })[];
  case_properties: CasePropertiesValueForm[];
  review_roles: (CaseTemplateReviewRole & { key: string })[];
  file_categories: FileCategoryFormValue[];
  case_actions: CaseTemplateActionFormValue[];
  workflow_rules: CaseTemplateWorkflowRuleFormValue;
  case_blocks_configs: { [key: string]: CaseBlockConfigOutput };
  case_tasks: CaseTemplateTaskFormValue[];
};

export type CaseTemplateFormValues = Omit<
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
  "attributes"
> & {
  attributes: CaseTemplateVersionAttributesForm;
};

/**
 * Transforms actions object and adds internal IDs, sorted by order
 */
const addInternalIdsToActions = (actions: {
  [key: string]: CaseTemplateActionOutput;
}): CaseTemplateActionFormValue[] => {
  return objectToArrayWithKey(actions)
    .map((action) => ({
      ...action,
      _internalId: generateInternalId(),
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

/**
 * Transforms tasks object and adds internal IDs, sorted by order
 */
const addInternalIdsToTasks = (
  tasks: { [key: string]: CaseTemplateTaskOutput } | undefined,
): CaseTemplateTaskFormValue[] => {
  return objectToArrayWithKey(tasks || {})
    .map((task) => ({
      ...task,
      _internalId: generateInternalId(),
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

/**
 * Creates mappings from task/action keys to their internal IDs
 */
const createKeyToIdMappings = (
  tasks: CaseTemplateTaskFormValue[],
  actions: CaseTemplateActionFormValue[],
) => {
  const taskKeyToInternalId = new Map<string, string>();
  const actionKeyToInternalId = new Map<string, string>();

  tasks.forEach((task) => {
    if (task.key) {
      taskKeyToInternalId.set(task.key, task._internalId);
    }
  });

  actions.forEach((action) => {
    if (action.key) {
      actionKeyToInternalId.set(action.key, action._internalId);
    }
  });

  return { taskKeyToInternalId, actionKeyToInternalId };
};

/**
 * Updates workflow rules to use internal IDs from the actual tasks/actions
 */
const addInternalIdsToWorkflowRules = (
  workflowRules: { [key: string]: CaseTemplateWorkflowRuleOutput } | undefined,
  taskKeyToInternalId: Map<string, string>,
  actionKeyToInternalId: Map<string, string>,
): CaseTemplateWorkflowRuleFormValue => {
  return Object.fromEntries(
    Object.entries(workflowRules || {}).map(([statusKey, rule]) => [
      statusKey,
      {
        ...rule,
        workflow_tasks_keys: rule.workflow_tasks_keys?.map((task) => ({
          ...task,
          _internalId: taskKeyToInternalId.get(task.task_key) || "",
        })),
        actions: rule.actions?.map((action) => ({
          ...action,
          _internalId: actionKeyToInternalId.get(action.action_key) || "",
          required_tasks_keys: action.required_tasks_keys?.map((reqTask) => ({
            ...reqTask,
            _internalId: taskKeyToInternalId.get(reqTask.task_key) || "",
          })),
        })),
      },
    ]),
  );
};

export const transformTemplateToFormValues = (
  template: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
): CaseTemplateFormValues => {
  const fileCategories = objectToArrayWithKey(
    template.attributes.file_categories,
  ) as FileCategoryFormValue[];

  fileCategories.forEach((category) => {
    if (category.key === "other_files") {
      (category as FileCategoryFormValue)._isDefault = true;
    }
  });

  // Transform actions and tasks with internal IDs
  const actionsWithIds = addInternalIdsToActions(
    template.attributes.case_actions,
  );
  const tasksWithIds = addInternalIdsToTasks(template.attributes.case_tasks);

  // Create key-to-ID mappings for workflow rules
  const { taskKeyToInternalId, actionKeyToInternalId } = createKeyToIdMappings(
    tasksWithIds,
    actionsWithIds,
  );

  // Add internal IDs to workflow rules
  const workflowRulesWithIds = addInternalIdsToWorkflowRules(
    template.attributes.workflow_rules,
    taskKeyToInternalId,
    actionKeyToInternalId,
  );

  return {
    ...template,
    attributes: {
      ...template.attributes,
      additional_inputs: objectToArrayWithKey(
        template.attributes.additional_inputs,
      ),
      review_roles: objectToArrayWithKey(template.attributes.review_roles),
      case_properties: objectToArrayWithKey(
        template.attributes.case_properties,
      ),
      file_categories: fileCategories,
      case_actions: actionsWithIds,
      workflow_rules: workflowRulesWithIds as CaseTemplateWorkflowRuleFormValue,
      case_tasks: tasksWithIds,
    },
  };
};

export const transformFormValuesToTemplate = (
  formValues: CaseTemplateFormValues,
): JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel => {
  return {
    ...formValues,
    attributes: {
      ...formValues.attributes,
      additional_inputs: arrayWithKeyToObject(
        formValues.attributes.additional_inputs,
      ),
      review_roles: arrayWithKeyToObject(formValues.attributes.review_roles),
      case_properties: arrayWithKeyToObject(
        formValues.attributes.case_properties,
      ),
      file_categories: arrayWithKeyToObject(
        formValues.attributes.file_categories,
      ),
      case_actions: arrayWithKeyToObject(
        formValues.attributes.case_actions?.map((action, index) => ({
          ...cleanFormFields(deepOmit(action, "_internalId")),
          order: index,
        })),
        {
          excludeKey: false,
        },
      ),
      workflow_rules: prepareWorkflowRules(
        formValues.attributes.workflow_rules,
      ),
      case_tasks: arrayWithKeyToObject(
        formValues.attributes.case_tasks?.map((task, index) => ({
          ...cleanFormFields(deepOmit(task, "_internalId")),
          order: index,
        })),
        {
          excludeKey: false,
        },
      ),
    },
  };
};

const objectToArrayWithKey = <T extends object>(object: Record<string, T>) => {
  return Object.entries(object || {}).map(([key, value]) => ({
    ...value,
    key,
  }));
};

const cleanFormField = (field: CaseTemplateFormFieldsInner) => {
  // Remove options array for field types that don't need it
  const selectFields = ["select", "multi_select"];
  if (selectFields.includes(field.type)) return field;

  const { options: _options, ...fieldWithoutOptions } =
    field as unknown as Record<string, unknown>;

  return fieldWithoutOptions;
};

const hasFormWithFields = (
  input: unknown,
): input is { form: { fields: unknown[] } } => {
  return (
    typeof input === "object" &&
    input !== null &&
    "form" in input &&
    typeof input.form === "object" &&
    input.form !== null &&
    "fields" in input.form &&
    Array.isArray(input.form.fields)
  );
};

const cleanFormFields = (
  input: CaseTemplateActionFormValue | CaseTemplateTaskFormValue,
) => {
  if (hasFormWithFields(input)) {
    return {
      ...input,
      form: {
        ...(input.form as unknown as Record<string, unknown>),
        fields: input.form.fields.map((field) =>
          cleanFormField(field as CaseTemplateFormFieldsInner),
        ),
      },
    };
  }
  return input;
};

const arrayWithKeyToObject = <
  T extends object & TemporaryFieldMarker & { key: string },
>(
  array: T[] | undefined,
  options = { excludeKey: true },
) => {
  return Object.fromEntries(
    (array || [])
      // drop _isNewlyAppended from the object
      .map(({ key, ...input }) => [
        key,
        {
          ...deepOmit(input, "_isNewlyAppended"),
          ...(options?.excludeKey ? {} : { key }),
        },
      ])
      .filter(([key]) => isString(key) && key.trim().length > 0),
  );
};

const prepareWorkflowRules = <T extends CaseTemplateWorkflowRuleFormValue>(
  values: T,
) => {
  return Object.fromEntries(
    Object.entries(values)
      .map(([key, rule]) => [
        key,
        {
          ...rule,
          workflow_tasks_keys: rule.workflow_tasks_keys
            ?.filter((task) => task.task_key)
            ?.map((task) => ({
              task_key: task.task_key,
              order: task.order,
            })),
          actions: rule.actions
            ?.filter((action) => action.action_key)
            ?.map((action) => ({
              action_key: action.action_key,
              automation: action.automation,
              required_tasks_keys: action.required_tasks_keys?.map(
                (reqTask) => ({
                  task_key: reqTask.task_key,
                }),
              ),
            })),
        },
      ])
      // Filter out rules with no actions or tasks
      .filter(
        ([, rule]) =>
          !!(rule as CaseTemplateWorkflowRuleFormValue[string]).actions
            ?.length ||
          !!(rule as CaseTemplateWorkflowRuleFormValue[string])
            .workflow_tasks_keys?.length,
      ),
  );
};
